/**
 * Error utility functions for consistent error handling across the application
 */

/**
 * Extracts a user-friendly error message from various error shapes
 * Handles API errors, validation errors, and generic errors
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred'
  }

  // Handle Error objects
  if (error instanceof Error) {
    return error.message
  }

  // Handle API error responses (axios/fetch style)
  if (typeof error === 'object' && error !== null) {
    // Check for nested error structures (common in API responses)
    const err = error as {
      response?: { data?: { error?: { message?: string }; message?: string } }
      message?: string
    }

    const apiMessage =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message

    if (apiMessage) {
      return String(apiMessage)
    }
  }

  // Fallback to string conversion
  return String(error)
}

/**
 * Type guard to check if error is an API error with response data
 */
export function isApiError(error: unknown): error is {
  response: { data: { message: string } }
} {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response: unknown }).response
    return (
      typeof response === 'object' && response !== null && 'data' in response
    )
  }
  return false
}
